// Reports page with a tile-based selector for the available reports
import { useState } from 'react';
import Navbar from '../components/Navbar';
import {
  FileText,
  ShieldOff,
  UserX,
  Shirt,
  AlertTriangle,
  Users,
  ClipboardList,
  ArrowLeft,
} from 'lucide-react';
import DeclinedPhotoWaiversReport from '../components/reports/DeclinedPhotoWaiversReport';
import ParentsWithoutPlayersReport from '../components/reports/ParentsWithoutPlayersReport';
import ShirtSummaryReport from '../components/reports/ShirtSummaryReport';
import MismatchedAgeGroupsReport from '../components/reports/MismatchedAgeGroupsReport';
import TeamPlayerCountsReport from '../components/reports/TeamPlayerCountsReport';
import PlayerDispositionReport from '../components/reports/PlayerDispositionReport';

type ReportId =
  | 'declined-waivers'
  | 'parents-no-players'
  | 'shirt-summary'
  | 'mismatched-age-groups'
  | 'team-player-counts'
  | 'player-disposition';

interface ReportDefinition {
  id: ReportId;
  title: string;
  description: string;
  Icon: typeof FileText;
  iconColor: string;
  component: () => JSX.Element;
}

const REPORTS: ReportDefinition[] = [
  {
    id: 'declined-waivers',
    title: 'Declined Photo Waivers',
    description: 'List of players whose parents have declined the photo release waiver.',
    Icon: ShieldOff,
    iconColor: 'text-rose-600',
    component: DeclinedPhotoWaiversReport,
  },
  {
    id: 'parents-no-players',
    title: 'Parents Without Players',
    description: 'Parent accounts that have not yet registered any players.',
    Icon: UserX,
    iconColor: 'text-amber-600',
    component: ParentsWithoutPlayersReport,
  },
  {
    id: 'shirt-summary',
    title: 'Shirt Size Summary',
    description: 'Aggregate counts of selected shirt sizes across all players.',
    Icon: Shirt,
    iconColor: 'text-emerald-600',
    component: ShirtSummaryReport,
  },
  {
    id: 'mismatched-age-groups',
    title: 'Mismatched Age Groups',
    description: 'Players whose assigned age group does not match their birthdate or sex.',
    Icon: AlertTriangle,
    iconColor: 'text-orange-600',
    component: MismatchedAgeGroupsReport,
  },
  {
    id: 'team-player-counts',
    title: 'Team Player Counts',
    description: 'Roster counts for every team, broken down by gender.',
    Icon: Users,
    iconColor: 'text-indigo-600',
    component: TeamPlayerCountsReport,
  },
  {
    id: 'player-disposition',
    title: 'Player Disposition',
    description:
      'Every registered player with evaluations attended and current team assignments.',
    Icon: ClipboardList,
    iconColor: 'text-sky-600',
    component: PlayerDispositionReport,
  },
];

export default function Reports() {
  const [selectedId, setSelectedId] = useState<ReportId | null>(null);
  const selected = REPORTS.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="mx-auto max-w-7xl py-10 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 mb-6">
          <FileText className="h-6 w-6 text-indigo-600" />
          <h1 className="text-2xl font-bold tracking-tight text-gray-800">
            Administrator Reports
          </h1>
        </div>

        {selected ? (
          <div>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="mb-4 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Reports
            </button>
            <selected.component />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REPORTS.map(({ id, title, description, Icon, iconColor }) => (
              <button
                key={id}
                type="button"
                onClick={() => setSelectedId(id)}
                className="bg-white shadow-md rounded-md p-5 flex flex-col items-center text-center hover:shadow-lg hover:-translate-y-0.5 transition focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <Icon className={`w-10 h-10 mb-4 ${iconColor}`} />
                <h4 className="text-lg font-semibold mb-2 text-gray-800">{title}</h4>
                <p className="text-gray-600 text-sm">{description}</p>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
